import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  FiFileText, 
  FiCheckCircle, 
  FiX, 
  FiLoader,
  FiTrash2,
  FiChevronRight,
  FiZap,
  FiEye,
  FiClock,
  FiShield,
  FiStar,
  FiArrowRight,
  FiUpload,
  FiInfo
} from "react-icons/fi";
import { 
  TbFileText, 
  TbFileDescription, 
  TbFileTypography 
} from "react-icons/tb";
import { motion, AnimatePresence } from "framer-motion";
import { summarizeFile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../services/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import AuthModal from "./AuthModal";
import Lottie from "lottie-react";
import ErrorAnimation from "../assets/lotties/Failed.json";
import SuccessAnimation from "../assets/lotties/SuccessAnimation.json";
import UploadAnimation from "../assets/lotties/Select-Document.json";
import { useNavigate } from "react-router-dom"; 

const UploadSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState("");
  const [summarizeLoading, setSummarizeLoading] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [fileErrorModal, setFileErrorModal] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showFeatures, setShowFeatures] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileChange = useCallback((selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
      setSummary("");
      setUploadSuccess(true);
      simulateUploadProgress();
    }
  }, []);

  const simulateUploadProgress = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const handleSummarize = async () => {
    if (!file) {
      setFileErrorModal("Veuillez sélectionner un fichier avant de continuer.");
      return;
    }
    
    if (!user) {
      setAuthModalOpen(true);
      return;
    }

    setSummarizeLoading(true);

    try {
      const res = await summarizeFile(file);
      setSummary(res.summary);

      const docRef = await addDoc(collection(db, "documents"), {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || "Utilisateur",
        fileName: file.name,
        summary: res.summary,
        uploadedAt: serverTimestamp(),
        conversation: [],
      });

      navigate("/history", { state: { newDocId: docRef.id } });

    } catch (err) {
      console.error(err);
      setFileErrorModal("Erreur lors du traitement du fichier. Veuillez réessayer.");
      setSummarizeLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;

    const allowedExtensions = ["pdf", "docx", "txt"];
    const fileExt = selectedFile.name.split(".").pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      setFileErrorModal("Format non supporté. Seuls PDF, DOCX et TXT sont acceptés.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setFileErrorModal("Fichier trop volumineux. Taille maximale : 10MB");
      return;
    }

    handleFileChange(selectedFile);
  };

  const clearFile = () => {
    setFile(null);
    setSummary("");
    setUploadSuccess(false);
    setShowFeatures(true);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch(ext) {
      case 'pdf': return <TbFileText className="text-red-500 text-2xl sm:text-3xl" />;
      case 'docx': return <TbFileDescription className="text-blue-500 text-2xl sm:text-3xl" />;
      case 'txt': return <TbFileTypography className="text-green-500 text-2xl sm:text-3xl" />;
      default: return <FiFileText className="text-gray-500 text-2xl sm:text-3xl" />;
    }
  };

  useEffect(() => {
    if (!user) {
      setFile(null);
      setSummary("");
      setUploadSuccess(false);
    }
  }, [user]);

  const features = [
    {
      icon: <FiZap className="text-amber-500 text-xl sm:text-2xl" />,
      title: "Résumé Intelligent",
      description: "Extraction des points clés",
      gradient: "from-amber-50 to-orange-50",
      iconBg: "bg-amber-100"
    },
    {
      icon: <FiClock className="text-purple-500 text-xl sm:text-2xl" />,
      title: "Gain de Temps",
      description: "Économisez des heures de lecture",
      gradient: "from-purple-50 to-pink-50",
      iconBg: "bg-purple-100"
    },
    {
      icon: <FiEye className="text-teal-500 text-xl sm:text-2xl" />,
      title: "Analyse Précise",
      description: "IA avancée pour des résultats fiables",
      gradient: "from-teal-50 to-cyan-50",
      iconBg: "bg-teal-100"
    },
    {
      icon: <FiShield className="text-green-500 text-xl sm:text-2xl" />,
      title: "Sécurisé",
      description: "Données chiffrées et protégées",
      gradient: "from-green-50 to-emerald-50",
      iconBg: "bg-green-100"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          {/* Header with Animation */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="text-center mb-10 sm:mb-14 lg:mb-20"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-100 to-blue-100 px-4 py-2 rounded-full mb-6 shadow-sm">
              <FiStar className="text-teal-600 text-sm" />
              <span className="text-xs sm:text-sm font-medium text-teal-700">Propulsé par l'IA</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-slate-900 via-teal-800 to-slate-900 bg-clip-text text-transparent">
                Transformez vos documents
              </span>
              <br />
              <span className="bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                en résumés intelligents
              </span>
            </h1>
            
            <p className="text-gray-600 text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Laissez l'IA analyser vos fichiers et extraire l'essentiel 
              <span className="hidden sm:inline"> en quelques secondes</span>
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Features Section */}
            <AnimatePresence>
              {showFeatures && (
                <motion.div 
                  initial="hidden"
                  animate="visible"
                  variants={containerVariants}
                  className="w-full lg:w-1/3"
                >
                  <div className="sticky top-24 bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100/50 overflow-hidden">
                    <div className="p-5 sm:p-6 lg:p-8">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                          Pourquoi nous choisir ?
                        </h2>
                        <div className="w-12 h-0.5 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full" />
                      </div>
                      
                      <div className="space-y-4">
                        {features.map((feature, index) => (
                          <motion.div
                            key={index}
                            variants={featureVariants}
                            transition={{ delay: index * 0.1 }}
                            className={`group relative bg-gradient-to-r ${feature.gradient} rounded-xl p-4 transition-all duration-300 hover:shadow-md hover:scale-[1.02]`}
                          >
                            <div className="flex items-start gap-4">
                              <div className={`p-2 rounded-lg ${feature.iconBg} shadow-sm flex-shrink-0`}>
                                {feature.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-800 text-sm sm:text-base mb-1">
                                  {feature.title}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600">
                                  {feature.description}
                                </p>
                              </div>
                              <FiChevronRight className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      <div className="mt-8 pt-6 border-t border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4">
                          Formats supportés
                        </h3>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { icon: TbFileText, name: "PDF", color: "red", ext: ".pdf" },
                            { icon: TbFileDescription, name: "DOCX", color: "blue", ext: ".docx" },
                            { icon: TbFileTypography, name: "TXT", color: "green", ext: ".txt" }
                          ].map((format, idx) => (
                            <div
                              key={idx}
                              className={`group relative text-center p-3 bg-gradient-to-br from-${format.color}-50 to-white rounded-xl border border-${format.color}-100 hover:shadow-md transition-all duration-300 cursor-pointer`}
                            >
                              <format.icon className={`text-${format.color}-500 text-xl mx-auto mb-2 group-hover:scale-110 transition-transform duration-300`} />
                              <span className="text-xs font-medium text-gray-700 block">{format.name}</span>
                              <span className="text-xs text-gray-500">{format.ext}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Upload Section */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              transition={{ delay: 0.2 }}
              className={`${showFeatures ? 'lg:w-2/3' : 'w-full'} transition-all duration-500`}
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Upload Header */}
                <div className="bg-gradient-to-r from-teal-600 to-blue-600 px-6 sm:px-8 py-4 sm:py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-bold text-lg sm:text-xl">
                        Nouveau document
                      </h3>
                      <p className="text-teal-100 text-sm">
                        Glissez-déposez votre fichier ici
                      </p>
                    </div>
                    <FiUpload className="text-white/60 text-2xl" />
                  </div>
                </div>

                <div className="p-6 sm:p-8">
                  {/* Drag & Drop Zone */}
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className={`relative border-3 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
                      isDragging 
                        ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-blue-50 shadow-inner scale-[1.02]' 
                        : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50/50'
                    } ${file ? 'border-green-400 bg-green-50/30' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !file && fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      id="file-input"
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => validateAndSetFile(e.target.files[0])}
                      className="hidden"
                    />

                    <AnimatePresence mode="wait">
                      {!file ? (
                        <motion.div
                          key="upload"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="space-y-6"
                        >
                          <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto">
                            <Lottie 
                              animationData={UploadAnimation} 
                              loop={true}
                              className="w-full h-full"
                            />
                          </div>
                          
                          <div className="space-y-3">
                            <p className="text-xl font-bold text-gray-800">
                              Déposez votre document
                            </p>
                            <p className="text-gray-500">
                              ou <span className="text-teal-600 font-semibold hover:underline">cliquez pour parcourir</span>
                            </p>
                          </div>

                          <div className="flex justify-center gap-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">📄 PDF</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">📝 DOCX</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">📃 TXT</span>
                          </div>

                          <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-xs text-gray-500">
                            <FiShield className="text-green-500" />
                            <span>100% sécurisé</span>
                            <FiZap className="text-amber-500" />
                            <span>Traitement rapide</span>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="file"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="space-y-6"
                        >
                          {/* Success Animation */}
                          <div className="w-20 h-20 mx-auto">
                            <Lottie 
                              animationData={SuccessAnimation} 
                              loop={false}
                              className="w-full h-full"
                            />
                          </div>

                          {/* File Card */}
                          <div className="max-w-md mx-auto w-full">
                            <div className="relative p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg border border-gray-200">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearFile();
                                }}
                                className="absolute -top-2 -right-2 p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                              >
                                <FiTrash2 className="text-red-500 text-sm" />
                              </motion.button>
                              
                              <div className="flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-gradient-to-br from-teal-100 to-blue-100 shadow-sm">
                                  {getFileIcon(file.name)}
                                </div>
                                
                                <div className="flex-1 min-w-0 text-left">
                                  <h4 className="font-bold text-gray-900 truncate">
                                    {file.name}
                                  </h4>
                                  <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <span className="text-xs text-gray-500">
                                      {formatFileSize(file.size)}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full">
                                      Prêt
                                    </span>
                                  </div>
                                </div>
                                
                                <FiCheckCircle className="text-green-500 text-2xl flex-shrink-0" />
                              </div>

                              {/* Upload Progress */}
                              {uploadProgress < 100 && (
                                <div className="mt-4">
                                  <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                    <motion.div 
                                      initial={{ width: 0 }}
                                      animate={{ width: `${uploadProgress}%` }}
                                      className="h-full bg-gradient-to-r from-teal-500 to-blue-500 rounded-full"
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Chargement... {uploadProgress}%
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Success Message */}
                          <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-xl border border-green-200"
                          >
                            <FiCheckCircle className="text-green-500 text-xl flex-shrink-0" />
                            <div className="text-left">
                              <p className="font-bold text-green-800">
                                Fichier chargé avec succès !
                              </p>
                              <p className="text-green-600 text-sm">
                                Prêt pour l'analyse par l'IA
                              </p>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                  >
                    <motion.button
                      whileHover={!summarizeLoading && file && uploadSuccess ? { scale: 1.02 } : {}}
                      whileTap={!summarizeLoading && file && uploadSuccess ? { scale: 0.98 } : {}}
                      onClick={handleSummarize}
                      disabled={summarizeLoading || !file || !uploadSuccess}
                      className={`
                        relative w-full py-4 px-6 rounded-xl font-bold text-base sm:text-lg
                        flex items-center justify-center gap-3
                        transition-all duration-300
                        overflow-hidden group
                        ${summarizeLoading ? 'cursor-wait' : ''}
                        ${
                          !file || !uploadSuccess
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white shadow-lg hover:shadow-xl'
                        }
                      `}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      <span className="relative z-10 flex items-center gap-2">
                        {summarizeLoading ? (
                          <>
                            <FiLoader className="animate-spin" />
                            <span>Analyse en cours...</span>
                          </>
                        ) : (
                          <>
                            <FiZap className="text-lg" />
                            <span>Analyser avec l'IA</span>
                            <FiArrowRight className="text-lg transition-transform duration-300 group-hover:translate-x-1" />
                          </>
                        )}
                      </span>
                    </motion.button>

                    {/* Info Message */}
                    {!user && file && uploadSuccess && (
                      <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center text-sm text-gray-500 mt-4 flex items-center justify-center gap-2"
                      >
                        <FiInfo className="text-teal-500" />
                        Connectez-vous pour sauvegarder votre analyse
                      </motion.p>
                    )}
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {authModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <AuthModal closeModal={() => setAuthModalOpen(false)} />
          </motion.div>
        )}

        {fileErrorModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setFileErrorModal("")}
            />
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl"
            >
              <button
                onClick={() => setFileErrorModal("")}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX className="text-gray-500" />
              </button>

              <div className="w-32 h-32 mx-auto mb-4">
                <Lottie animationData={ErrorAnimation} loop={false} />
              </div>

              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Une erreur est survenue
                </h3>
                <p className="text-gray-600 mb-6">
                  {fileErrorModal}
                </p>
                <button
                  onClick={() => setFileErrorModal("")}
                  className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-lg font-bold hover:shadow-lg transition-all duration-300"
                >
                  Compris
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UploadSection;